import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { BatchEditableTableServiceInterface } from '../controls/inline-editable-table/services/batch-editable-table-service-interface';
import { Box } from '../models/box';
import { BoxAccessibility } from '../models/box-accessibility';
import { IBoxAccessibility } from '../models/interfaces/box-accessibility.interface';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class BoxesService extends CrudService implements AdvancedService<Box>, BatchEditableTableServiceInterface<Box> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<Box>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        if (context === null) {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<Box>(Box, x);
                    return res;
                })
            );
        } else {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/BoxGroups/${context.value}/Boxes`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<Box>(Box, x);
                    return res;
                })
            );
        }
    }

    ListForOrganisation(context: Context, filters: FilterInfos[], sortInfos: SortInfos, generalFilters: FilterInfos[], pageNumber = 0, pageSize = 100): Observable<Results<Box>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        if (context === null) {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Boxes`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<Box>(Box, x);
                    return res;
                })
            );
        } else {
            return of(null);
        }
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        if (context === null) {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/export`, { headers: headers, responseType: 'blob' }).pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
        } else {
            // Appel de la Web Api
            return this.http
                .get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/BoxGroups/${context.value}/Boxes/export`, {
                    headers: headers,
                    responseType: 'blob',
                })
                .pipe(
                    map((res: any) => {
                        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        return blob;
                    })
                );
        }
    }

    update(boxId: string, box: Box): Observable<Box> {
        // Appel de la Web Api
        return this.http.put<Box>(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/${boxId}`, box, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    delete(boxId: string): Observable<boolean> {
        // Appel de la Web Api
        return this.http.delete<Box>(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/${boxId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return true;
            })
        );
    }

    add(box: Box): Observable<Box> {
        // Appel de la Web Api
        return this.http.post<Box>(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes`, box, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    updateAccessibilities(boxId: string, accessibilities: IBoxAccessibility[]): Observable<IBoxAccessibility[]> {
        // Appel de la Web Api
        return this.http
            .post<IBoxAccessibility[]>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/${boxId}/Accessibilities`,
                accessibilities,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    get(boxId: string): Observable<Box> {
        // Appel de la Web Api
        return this.http.get<Box>(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/${boxId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    getAccessibilities(boxId: string): Observable<BoxAccessibility[]> {
        // Appel de la Web Api
        return this.http
            .get<BoxAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/${boxId}/accessibilities`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    exportAccessibilities(boxId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http
            .get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Boxes/${boxId}/accessibilities/export`, {
                responseType: 'blob',
            })
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    public applyBatchModifications(context: Context, modifications: BatchModification<Box>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        modifications.deletes.forEach((x) => {
            requests.push(this.delete(x.data.id).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        modifications.updates.forEach((x) => {
            requests.push(this.update(x.data.id, x.data).pipe(map((y) => new BatchResult(x.data.editableId, true))));
        });

        modifications.creates.forEach((x) => {
            x.data.groupId = context.value as string;
            requests.push(this.add(x.data).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        return forkJoin(requests);
    }

    applyOrderChanges(boxes: Box[]) {
        // Appel de la Web Api
        return this.http
            .patch<Box[]>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/boxes/`, boxes, this.getDefaultHttpOptions())
            .pipe(map((x) => {}));
    }
}
